import React, { useState } from 'react';
import { ActionButton, Stack } from '@fluentui/react';
import { IOfferResourceProps } from '../../../../../props/offers';
import Text from '../../../../typography/text';
import Label from '../../../../typography/label';
import Tag from '../../../../uiframeworks/tag';
import GeneralService from '../../../../../services/general';

interface OrderDetailsServicesBreakdownProps {
    offer: IOfferResourceProps;
}

interface IServiceCategoryItem {
    key: string;
    label: string;
    isIncluded: boolean;
    items?: Array<{
        vendorName?: string;
        qtyText?: string;
        notes?: string;
        finalPrice?: string | number;
    }>;
}

const OrderDetailsServicesBreakdown: React.FC<OrderDetailsServicesBreakdownProps> = (props: OrderDetailsServicesBreakdownProps) => {
    const { offer } = props;
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isClosed, setIsClosed] = useState<boolean>(true);

    if (isClosed) {
        return <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'} styles={{ root: { padding: '4px 0px' } }}>
            <Text size={'small'} style={{ fontStyle: 'italic', color: '#605e5c' }}>Services Breakdown is hidden.</Text>
            <ActionButton iconProps={{ iconName: 'Redo' }} text={'Show Services Breakdown'} onClick={() => setIsClosed(false)} />
        </Stack>;
    }

    const originTruckingCount = offer.originTruckingCosts?.length || 0;
    const destTruckingCount = offer.destinationTruckingCosts?.length || 0;
    const loadingLabourCount = offer.loadingLabourCosts?.length || 0;
    const unloadingLabourCount = offer.unloadingLabourCosts?.length || 0;
    const containerShippingCount = offer.containerShippingCosts?.length || 0;
    const charteredShipCount = offer.charteredShipCosts?.length || 0;
    const insuranceCount = offer.insuranceCosts?.length || 0;
    const additionalCount = offer.additionalCosts?.length || 0;

    const categories: IServiceCategoryItem[] = [
        {
            key: 'originTrucking',
            label: 'Trucking at Origin',
            isIncluded: originTruckingCount > 0,
            items: offer.originTruckingCosts?.map((item) => ({
                vendorName: item.vendor?.name,
                qtyText: `${GeneralService.getNumberWithSeparator(Number(item.quantity))} ${item.quantityUnit?.name || ''}${item.priceType === 'perquantity' ? ` @ Rp. ${GeneralService.getNumberWithSeparator(Number(item.price))}` : ''}`,
                notes: item.notes,
                finalPrice: item.finalPrice || item.totalPrice
            }))
        },
        {
            key: 'loadingLabour',
            label: 'Loading Labour',
            isIncluded: loadingLabourCount > 0,
            items: offer.loadingLabourCosts?.map((item) => ({
                vendorName: item.vendor?.name,
                qtyText: `${GeneralService.getNumberWithSeparator(Number(item.quantity))} ${item.quantityUnit?.name || ''}${item.priceType === 'perquantity' ? ` @ Rp. ${GeneralService.getNumberWithSeparator(Number(item.price))}` : ''}`,
                notes: item.notes,
                finalPrice: item.finalPrice || item.totalPrice
            }))
        },
        {
            key: 'destinationTrucking',
            label: 'Trucking at Destination',
            isIncluded: destTruckingCount > 0,
            items: offer.destinationTruckingCosts?.map((item) => ({
                vendorName: item.vendor?.name,
                qtyText: `${GeneralService.getNumberWithSeparator(Number(item.quantity))} ${item.quantityUnit?.name || ''}${item.priceType === 'perquantity' ? ` @ Rp. ${GeneralService.getNumberWithSeparator(Number(item.price))}` : ''}`,
                notes: item.notes,
                finalPrice: item.finalPrice || item.totalPrice
            }))
        },
        {
            key: 'unloadingLabour',
            label: 'Unloading Labour',
            isIncluded: unloadingLabourCount > 0,
            items: offer.unloadingLabourCosts?.map((item) => ({
                vendorName: item.vendor?.name,
                qtyText: `${GeneralService.getNumberWithSeparator(Number(item.quantity))} ${item.quantityUnit?.name || ''}${item.priceType === 'perquantity' ? ` @ Rp. ${GeneralService.getNumberWithSeparator(Number(item.price))}` : ''}`,
                notes: item.notes,
                finalPrice: item.finalPrice || item.totalPrice
            }))
        },
        {
            key: 'containerShipping',
            label: 'Container Shipping',
            isIncluded: containerShippingCount > 0,
            items: offer.containerShippingCosts?.map((item) => ({
                vendorName: item.vendor?.name,
                qtyText: `${GeneralService.getNumberWithSeparator(Number(item.quantity))} ${item.quantityUnit?.name || ''}${item.priceType === 'perquantity' ? ` @ Rp. ${GeneralService.getNumberWithSeparator(Number(item.price))}` : ''}`,
                finalPrice: item.finalPrice || item.totalPrice
            }))
        },
        {
            key: 'charteredShip',
            label: 'Chartered Ship',
            isIncluded: charteredShipCount > 0,
            items: offer.charteredShipCosts?.map((item) => ({
                vendorName: item.vendor?.name,
                qtyText: `${GeneralService.getNumberWithSeparator(Number(item.quantity))} ${item.quantityUnit?.name || ''}${item.priceType === 'perquantity' ? ` @ Rp. ${GeneralService.getNumberWithSeparator(Number(item.price))}` : ''}`,
                finalPrice: item.finalPrice || item.totalPrice
            }))
        },
        {
            key: 'insurance',
            label: 'Insurance',
            isIncluded: insuranceCount > 0,
            items: offer.insuranceCosts?.map((item) => ({
                vendorName: item.vendor?.name,
                qtyText: item.goodsValue ? `Nilai Barang: Rp. ${GeneralService.getNumberWithSeparator(Number(item.goodsValue))}` : undefined,
                notes: item.notes,
                finalPrice: item.finalPrice || item.totalPrice
            }))
        },
        {
            key: 'additionalCosts',
            label: 'Additional Costs',
            isIncluded: additionalCount > 0,
            items: offer.additionalCosts?.map((item) => ({
                vendorName: item.vendor?.name,
                qtyText: `${GeneralService.getNumberWithSeparator(Number(item.quantity))} ${item.quantityUnit?.name || ''}`,
                notes: item.name || item.notes,
                finalPrice: item.finalPrice || item.totalPrice
            }))
        }
    ];

    const activeCategories = categories.filter((c) => c.isIncluded);

    return <Stack tokens={{ childrenGap: 15 }}>
        {/* Header Action Bar (Minimize / Expand / Close controls) */}
        <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'}>
            <Stack tokens={{ childrenGap: 8 }}>
                <Label size={'small'}>Included Services:</Label>
                <Stack horizontal wrap tokens={{ childrenGap: 8 }}>
                    {activeCategories.map((cat) => (
                        <Tag
                            key={cat.key}
                            size={'small'}
                            variant={'success'}
                            text={`✓ ${cat.label}`}
                        />
                    ))}
                    {offer.includePPn ? <Tag size={'small'} variant={'success'} text={'✓ PPn'} /> : null}
                    {offer.includePPh ? <Tag size={'small'} variant={'success'} text={'✓ PPh'} /> : null}
                </Stack>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign={'center'}>
                <ActionButton
                    iconProps={{ iconName: isCollapsed ? 'ChevronDown' : 'ChevronUp' }}
                    text={isCollapsed ? 'Expand' : 'Minimize'}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                />
                <ActionButton
                    iconProps={{ iconName: 'Cancel' }}
                    text={'Close'}
                    onClick={() => setIsClosed(true)}
                />
            </Stack>
        </Stack>

        {/* Costing Breakdown Items (Collapsible Section) */}
        {!isCollapsed ? (
            activeCategories.length > 0 ? (
                <Stack tokens={{ childrenGap: 12 }}>
                    <Stack className={'divider'} />
                    {activeCategories.map((cat) => (
                        <Stack key={cat.key} tokens={{ childrenGap: 6 }}>
                            <Label size={'small'}>{cat.label}</Label>
                            {cat.items && cat.items.length > 0 ? (
                                cat.items.map((item, idx) => (
                                    <Stack
                                        key={idx}
                                        horizontal
                                        horizontalAlign={'space-between'}
                                        verticalAlign={'center'}
                                        styles={{
                                            root: {
                                                padding: '8px 12px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: 4,
                                                borderLeft: '3px solid #0078d4'
                                            }
                                        }}
                                    >
                                        <Stack tokens={{ childrenGap: 2 }}>
                                            {item.vendorName ? <Text size={'small'}>Vendor: <strong>{item.vendorName}</strong></Text> : null}
                                            {item.qtyText ? <Text size={'small'} style={{ color: '#605e5c' }}>Qty: {item.qtyText}</Text> : null}
                                            {item.notes ? <Text size={'small'} style={{ fontStyle: 'italic' }}>Notes: {item.notes}</Text> : null}
                                        </Stack>
                                        <Label size={'small'}>
                                            Rp. {GeneralService.getNumberWithSeparator(Number(item.finalPrice || 0))}
                                        </Label>
                                    </Stack>
                                ))
                            ) : (
                                <Text size={'small'} style={{ fontStyle: 'italic' }}>Tidak ada rincian biaya</Text>
                            )}
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <Text size={'small'} style={{ fontStyle: 'italic' }}>Tidak ada rincian layanan costing dari offer.</Text>
            )
        ) : null}
    </Stack>;
};

export default OrderDetailsServicesBreakdown;
